// ─── Medicine Card Data ───────────────────────────────────────────────────────

export const RARITY = {
  common:    { label: "Common",    color: "#6b7280", border: "#d1d5db", bg: "#f9fafb", glow: "rgba(209,213,219,0.6)" },
  uncommon:  { label: "Uncommon",  color: "#3b82f6", border: "#93c5fd", bg: "#eff6ff", glow: "rgba(147,197,253,0.6)" },
  rare:      { label: "Rare",      color: "#8b5cf6", border: "#c4b5fd", bg: "#f5f3ff", glow: "rgba(196,181,253,0.6)" },
  legendary: { label: "Legendary", color: "#f59e0b", border: "#fcd34d", bg: "#fffbeb", glow: "rgba(252,211,77,0.7)"  },
};

export const MEDICINE_CARDS = [
  // ── COMMON ─────────────────────────────────────────────────────────────────
  {
    id: "card-paracetamol", name: "Paracetamol", rarity: "common", emoji: "💊", image: "/medicinecards/Paracetamol.png",
    category: "Analgesic",
    tagline: "The world's most-used painkiller",
    fact: "Paracetamol's exact mechanism is still debated — it likely inhibits COX enzymes centrally. Despite its safe reputation, overdose is the #1 cause of acute liver failure in the UK. N-acetylcysteine is the antidote.",
  },
  {
    id: "card-ibuprofen", name: "Ibuprofen", rarity: "common", emoji: "🔥", image: "/medicinecards/Ibuprofen.png",
    category: "NSAID",
    tagline: "Anti-inflammatory with a sting in the tail",
    fact: "Ibuprofen inhibits COX-1 and COX-2, reducing prostaglandins. COX-1 inhibition strips the stomach's mucosal protection — always co-prescribe a PPI for regular users who are elderly or have a GI history.",
  },
  {
    id: "card-aspirin", name: "Aspirin", rarity: "common", emoji: "🩸", image: "/medicinecards/Aspirin.png",
    category: "Antiplatelet",
    tagline: "Irreversibly sticks to platelets for life",
    fact: "Low-dose aspirin (75 mg) irreversibly acetylates COX-1 in platelets. Since platelets have no nucleus they can't make new COX, so the antiplatelet effect lasts the full 7–10 day platelet lifespan. Dual antiplatelet therapy adds clopidogrel post-ACS.",
  },
  {
    id: "card-amoxicillin", name: "Amoxicillin", rarity: "common", emoji: "🦠", image: "/medicinecards/Amoxicillin.png",
    category: "Penicillin",
    tagline: "The bread-and-butter antibiotic",
    fact: "Amoxicillin is a broad-spectrum aminopenicillin destroyed by beta-lactamase. Adding clavulanate (co-amoxiclav) overcomes this. A rash during amoxicillin treatment in glandular fever (EBV) is NOT a penicillin allergy — it's a drug–virus interaction.",
  },
  {
    id: "card-omeprazole", name: "Omeprazole", rarity: "common", emoji: "🫙", image: "/medicinecards/Omeprazole.png",
    category: "Proton Pump Inhibitor",
    tagline: "Permanently silences the stomach's acid pump",
    fact: "Omeprazole irreversibly inhibits the H⁺/K⁺ ATPase pump. Long-term use raises the risk of hypomagnesaemia, fractures, C. difficile infection and B12 deficiency. Always review whether a PPI is still needed — many patients take them indefinitely without indication.",
  },
  {
    id: "card-metformin", name: "Metformin", rarity: "common", emoji: "🍬", image: "/medicinecards/Metformin.png",
    category: "Biguanide",
    tagline: "First-line for type 2 diabetes since 1957",
    fact: "Metformin reduces hepatic glucose output and improves insulin sensitivity. It does not cause hypoglycaemia alone and has proven cardiovascular benefits. Withhold before iodinated contrast and in renal impairment (eGFR < 30) — lactic acidosis is rare but fatal.",
  },
  {
    id: "card-salbutamol", name: "Salbutamol", rarity: "common", emoji: "💨", image: "/medicinecards/Salbutamol.png",
    category: "Beta-2 Agonist",
    tagline: "The blue reliever inhaler",
    fact: "Salbutamol is a short-acting beta-2 agonist (SABA) that relaxes bronchial smooth muscle within minutes. Using more than 3 puffs/week is a red flag for poor asthma control and should prompt a step-up in treatment.",
  },
  {
    id: "card-trimethoprim", name: "Trimethoprim", rarity: "common", emoji: "🚽", image: "/medicinecards/Trimethoprim.png",
    category: "Antibiotic",
    tagline: "First-line for uncomplicated UTI",
    fact: "Trimethoprim inhibits dihydrofolate reductase — the bacterial enzyme for folate synthesis. Avoid in pregnancy (folate antagonist). Resistance is growing; if local resistance > 20%, use nitrofurantoin instead. Duration is typically 3 days in women.",
  },

  // ── UNCOMMON ───────────────────────────────────────────────────────────────
  {
    id: "card-warfarin", name: "Warfarin", rarity: "uncommon", emoji: "🩺", image: "/medicinecards/Warfarin.png",
    category: "Anticoagulant",
    tagline: "Vitamin K's arch-nemesis",
    fact: "Warfarin inhibits vitamin K epoxide reductase, blocking activation of factors II, VII, IX and X. INR monitoring is essential — target 2–3 for most indications, 2.5–3.5 for mechanical heart valves. Dozens of drug and food interactions; always check before prescribing.",
  },
  {
    id: "card-atorvastatin", name: "Atorvastatin", rarity: "uncommon", emoji: "🫀", image: "/medicinecards/atorvastatin.png",
    category: "Statin",
    tagline: "LDL's greatest enemy",
    fact: "Atorvastatin inhibits HMG-CoA reductase, the rate-limiting step in cholesterol synthesis. Unlike simvastatin, it can be taken at any time of day. Always warn about myalgia — if myalgia plus raised CK occurs, stop immediately and investigate for rhabdomyolysis.",
  },
  {
    id: "card-ramipril", name: "Ramipril", rarity: "uncommon", emoji: "💓", image: "/medicinecards/Ramipril.png",
    category: "ACE Inhibitor",
    tagline: "Protects heart, kidneys and vessels",
    fact: "Ramipril blocks ACE, preventing angiotensin I → II conversion. It reduces cardiac remodelling post-MI and slows diabetic nephropathy. The dry cough (bradykinin accumulation) affects 10–15% — switch to an ARB (e.g. losartan) if intolerable.",
  },
  {
    id: "card-prednisolone", name: "Prednisolone", rarity: "uncommon", emoji: "⚡", image: "/medicinecards/Prednisolone.png",
    category: "Corticosteroid",
    tagline: "Powerful anti-inflammatory, powerful side effects",
    fact: "Prednisolone suppresses inflammation by inhibiting phospholipase A2. Long-term use causes Cushingoid syndrome: truncal obesity, striae, hypertension, osteoporosis and adrenal suppression. Never stop abruptly after > 3 weeks — taper to allow adrenal recovery.",
  },
  {
    id: "card-furosemide", name: "Furosemide", rarity: "uncommon", emoji: "💧", image: "/medicinecards/Furosemide.png",
    category: "Loop Diuretic",
    tagline: "The most powerful diuretic in medicine",
    fact: "Furosemide inhibits the Na⁺/K⁺/2Cl⁻ cotransporter in the thick ascending loop of Henle. It causes hypokalaemia — monitor electrolytes. IV furosemide is life-saving in acute pulmonary oedema, working partly by venodilation before its diuretic effect kicks in.",
  },
  {
    id: "card-metronidazole", name: "Metronidazole", rarity: "uncommon", emoji: "🌿", image: "/medicinecards/Metronidazole.png",
    category: "Antibiotic / Antiprotozoal",
    tagline: "The anaerobe assassin",
    fact: "Metronidazole is selectively reduced by anaerobic bacteria and protozoa to a toxic intermediate that damages DNA. It covers anaerobes (dental, abdominal) and protozoa (C. diff, Giardia, Trichomonas). Never drink alcohol — causes a severe Antabuse (disulfiram-like) reaction.",
  },
  {
    id: "card-amlodipine", name: "Amlodipine", rarity: "uncommon", emoji: "🌊", image: "/medicinecards/amlodipine.png",
    category: "Calcium Channel Blocker",
    tagline: "Smooth muscle relaxation in a tablet",
    fact: "Amlodipine blocks L-type voltage-gated calcium channels in vascular smooth muscle, causing vasodilation. It is first-line for hypertension in Afro-Caribbean patients and those with angina. Ankle oedema is common and results from increased capillary pressure, not fluid overload.",
  },

  // ── RARE ───────────────────────────────────────────────────────────────────
  {
    id: "card-vancomycin", name: "Vancomycin", rarity: "rare", emoji: "🛡️", image: "/medicinecards/Vancomycin.png",
    category: "Glycopeptide",
    tagline: "Last resort against Gram-positive superbugs",
    fact: "Vancomycin inhibits cell wall synthesis by binding the D-Ala-D-Ala terminus of peptidoglycan — a completely different target to beta-lactams. VRE emerged from overuse. Requires therapeutic drug monitoring; trough levels prevent nephrotoxicity and ensure efficacy.",
  },
  {
    id: "card-morphine", name: "Morphine", rarity: "rare", emoji: "🌺", image: "/medicinecards/Morphine.png",
    category: "Opioid Analgesic",
    tagline: "The gold standard for severe pain",
    fact: "Morphine binds mu-opioid receptors in the CNS and enteric nervous system. Tolerance develops to analgesia, sedation and respiratory depression — but not constipation. Always prescribe a stimulant laxative from day one. Naloxone reverses respiratory depression.",
  },
  {
    id: "card-digoxin", name: "Digoxin", rarity: "rare", emoji: "🌼", image: "/medicinecards/Digoxin.png",
    category: "Cardiac Glycoside",
    tagline: "From foxglove to the wards",
    fact: "Digoxin inhibits Na⁺/K⁺ ATPase, indirectly increasing intracellular calcium and cardiac contractility. It has an extremely narrow therapeutic window. Classic toxicity signs include the famous yellow-green visual halos, bradyarrhythmias and nausea.",
  },
  {
    id: "card-lithium", name: "Lithium", rarity: "rare", emoji: "⚛️", image: "/medicinecards/Lithium.png",
    category: "Mood Stabiliser",
    tagline: "An element that tames bipolar disorder",
    fact: "Lithium's mechanism is incompletely understood — it likely inhibits inositol monophosphatase and GSK-3β. It has the narrowest therapeutic window of any common drug: levels > 1.5 mmol/L cause tremor, polyuria and confusion; > 2.0 causes seizures and renal failure.",
  },
  {
    id: "card-methotrexate", name: "Methotrexate", rarity: "rare", emoji: "🧬", image: "/medicinecards/Methotrexate.png",
    category: "DMARD / Chemotherapy",
    tagline: "Folate antagonist with two careers",
    fact: "Methotrexate competitively inhibits dihydrofolate reductase, blocking DNA synthesis. At low weekly doses it is a DMARD for rheumatoid arthritis; at high daily doses it is chemotherapy. Folic acid supplementation (given on non-MTX days) reduces mucosal and hepatic toxicity.",
  },

  // ── LEGENDARY ──────────────────────────────────────────────────────────────
  {
    id: "card-penicillin", name: "Penicillin", rarity: "legendary", emoji: "🍄", image: "/medicinecards/Penicillin.png",
    category: "First Antibiotic",
    tagline: "The mould that changed everything",
    fact: "Alexander Fleming noticed Penicillium notatum killing bacteria on a contaminated plate in 1928. Penicillin entered clinical use in 1941 — Florey and Chain worked out how to purify enough to treat patients. The three shared the 1945 Nobel Prize. It is estimated to have saved over 200 million lives.",
  },
  {
    id: "card-insulin", name: "Insulin", rarity: "legendary", emoji: "🩸", image: "/medicinecards/Insulin.png",
    category: "Hormone Therapy",
    tagline: "The discovery that ended diabetes as a death sentence",
    fact: "Before 1921, type 1 diabetes meant death — patients were starved to extend life by months. Banting and Best isolated insulin from dog pancreas in Toronto; the first human patient, 14-year-old Leonard Thompson, was treated in January 1922. Banting received the Nobel Prize the following year.",
  },
  {
    id: "card-ciclosporin", name: "Ciclosporin", rarity: "legendary", emoji: "🌱", image: "/medicinecards/Ciclosporin.png",
    category: "Immunosuppressant",
    tagline: "The drug that made transplant surgery routine",
    fact: "Ciclosporin was isolated from a Norwegian soil fungus in 1972. It inhibits calcineurin, blocking T-cell activation and preventing organ rejection. Before its introduction, transplant outcomes were catastrophic. It enabled routine kidney, heart and liver transplantation — transforming the lives of millions.",
  },
  {
    id: "card-chlorpromazine", name: "Chlorpromazine", rarity: "legendary", emoji: "🧠", image: "/medicinecards/Chlorpromazine.png",
    category: "First Antipsychotic",
    tagline: "The drug that emptied psychiatric hospitals",
    fact: "Chlorpromazine was discovered in 1952 — the world's first drug to effectively treat psychosis. By blocking dopamine D2 receptors, it dramatically reduced positive symptoms of schizophrenia. Its discovery ended the era of institutionalisation and began the entire field of psychopharmacology.",
  },
];

// ── Drop logic ──────────────────────────────────────────────────────────────
export const rollCardDrop = (existingCardIds = []) => {
  const roll = Math.random();
  let rarity;
  if      (roll < 0.01) rarity = "legendary";
  else if (roll < 0.06) rarity = "rare";
  else if (roll < 0.20) rarity = "uncommon";
  else if (roll < 0.50) rarity = "common";
  else return null; // ~50% no drop

  const tiers = ["legendary", "rare", "uncommon", "common"];
  for (let i = tiers.indexOf(rarity); i < tiers.length; i++) {
    const pool = MEDICINE_CARDS.filter(c => c.rarity === tiers[i] && !existingCardIds.includes(c.id));
    if (pool.length > 0) return pool[Math.floor(Math.random() * pool.length)];
  }
  return null; // complete collection
};
